import React, { useState, useMemo, useCallback } from 'react';
import { Form, Formik, FormikHelpers, useFormikContext } from 'formik';

import { FormikTextField } from 'components/fields/FormikTextField';
import { FormikSelectField } from 'components/fields/FormikSelectField';
import {
    orgNameValidator,
    orgTypeValidator,
    orgEnrollEmailValidator,
} from 'common/modules/form-validation/validators';
import { FieldSpacer } from 'components/fields/FieldSpacer';
import { validateEmail } from 'common/api/users/email/validate';
import { hasErrorWithStatus } from 'common/api/utils/hasErrorWithStatus';
import { StatusCode } from 'common/api/config';
import { FormBtn } from 'components/form-btn';
import { getUrlToLoginPage } from 'features/auth/components/withAuth';
import { FadeIn } from 'components/loading-screen/FadeIn';
import { HubspotContactInfo } from 'common/api/crm/organization/enrollment/models';
import { useRootSelector } from 'common/features/featuresReducer';
import {
    selectOrganization,
    selectUserInformation,
    selectOrgTypeOptions,
    selectEnrollmentFlowControls,
    selectSocialRegistrationError,
    selectSocialRegistrationExistingAccountProvider,
    setSocialRegistrationError,
    setSocialRegistrationExistingAccountProvider,
    setUserData,
    submitLeadOrgCaptureInfo,
    selectProgressBarRef,
} from 'common/features/store/duck/ui/org-enrollment-v2';
import { useDispatch, useSelector } from 'react-redux';
import { selectIsOrganizationTypesLoading } from 'common/features/store/duck/organization/duck';
import { ContentfulSubheading } from '../components/ContentfulSubheading';
import { scrollTo } from '../utils/ScrollTo';
import { Routes } from 'routes';
import { ExternalLinks } from 'externalLinks';
import { SocialLoginButtons } from 'components/social-login/SocialLoginButtons';
import { SocialRegistrationProvider } from 'common/api/auth/socialRegistration';
import {
    buildEnrollmentV2SocialRegistrationChallengeUrl,
    createSocialRegistrationChallenge,
    persistEnrollmentV2SocialRegistrationState,
} from '../socialRegistration';
import { buildPostLoginContinueReturnPath } from 'features/coordinator/components/useContinueOrgEnrollment';
import { logOrgEnrollmentV2 } from 'config/analytics';
import { useAlert } from 'modules/alerts';
import { useSocialRegistrationProviders } from 'features/social-registration/useSocialRegistrationProviders';

const getSocialProviderLabel = (provider?: 'google' | 'apple' | null) => {
    if (provider === 'apple') {
        return 'Apple';
    }

    if (provider === 'google') {
        return 'Google';
    }

    return 'social';
};

type ValidateEmailResponse = Awaited<ReturnType<typeof validateEmail>>;

export const LeadCaptureInfo = () => {
    const dispatch = useDispatch<any>();
    const organization = useRootSelector(selectOrganization);
    const userInformation = useRootSelector(selectUserInformation);
    const { currentStep, maxStep } = useRootSelector(selectEnrollmentFlowControls);
    const socialRegistrationError = useRootSelector(selectSocialRegistrationError);
    const socialRegistrationExistingAccountProvider = useRootSelector(
        selectSocialRegistrationExistingAccountProvider
    );
    const [submitting, setSubmitting] = useState<boolean>(false);
    const progressBarRef = useSelector(selectProgressBarRef);
    const socialRegistrationProviders = useSocialRegistrationProviders();
    const googleProvider = socialRegistrationProviders.find(
        ({ provider }) => provider === 'google'
    );
    const appleProvider = socialRegistrationProviders.find(({ provider }) => provider === 'apple');
    const hasAvailableSocialProviders = Boolean(googleProvider || appleProvider);
    const isSocialRegistration =
        userInformation.registrationMethod === 'social' &&
        Boolean(userInformation.externalRegistrationKey);
    const hasReadOnlySocialEmail =
        isSocialRegistration &&
        !userInformation.emailRequired &&
        Boolean(userInformation.socialEmail);
    const providerLabel = useMemo(
        () => getSocialProviderLabel(userInformation.provider),
        [userInformation.provider]
    );
    const alert = useAlert();

    const initialValues: HubspotContactInfo = {
        coordinatorEmailAddress: userInformation.email,
        organizationName: organization.name,
        organizationTypeId: organization.organizationTypeId,
        countryCode: organization.countryCode,
    };

    const onSubmit = async (
        formData: HubspotContactInfo,
        formCtx: FormikHelpers<HubspotContactInfo>
    ) => {
        setSubmitting(true);
        dispatch(setSocialRegistrationExistingAccountProvider(null));
        const coordinatorEmailAddress = hasReadOnlySocialEmail
            ? userInformation.socialEmail || userInformation.email
            : formData.coordinatorEmailAddress;
        const res: ValidateEmailResponse | null =
            !isSocialRegistration || userInformation.emailRequired
                ? await validateEmail(coordinatorEmailAddress)
                : null;

        if (res && hasErrorWithStatus(res, StatusCode.Conflict)) {
            formCtx.setFieldError('coordinatorEmailAddress', ' ');
            setSubmitting(false);
            return;
        }

        if (res?.error?.body?.errors?.Email) {
            formCtx.setFieldError('coordinatorEmailAddress', 'Please enter a valid email address.');
            setSubmitting(false);
            return;
        }

        const result = await dispatch(
            submitLeadOrgCaptureInfo({
                ...formData,
                coordinatorEmailAddress,
            })
        );
        setSubmitting(false);

        if (result?.error) {
            alert({
                message:
                    'We couldn’t submit your information right now. Please check your connection and try again.',
            });
            return;
        }

        logOrgEnrollmentV2('org_enrollment:lead_capture_succeeded');
        scrollTo(progressBarRef);
    };

    const startSocialRegistration = useCallback(
        (provider: SocialRegistrationProvider, formValues: HubspotContactInfo) => {
            dispatch(setSocialRegistrationError(null));
            dispatch(setSocialRegistrationExistingAccountProvider(null));
            const challenge = createSocialRegistrationChallenge();
            persistEnrollmentV2SocialRegistrationState({
                organization: {
                    ...organization,
                    name: formValues.organizationName,
                    organizationTypeId: formValues.organizationTypeId,
                },
                userInformation: {
                    ...userInformation,
                    email: formValues.coordinatorEmailAddress,
                    username: formValues.coordinatorEmailAddress,
                },
                currentStep,
                maxStep,
                codeVerifier: challenge.codeVerifier,
            });
            const selectedProviderScheme = socialRegistrationProviders.find(
                (option) => option.provider === provider
            )?.scheme;
            window.location.assign(
                buildEnrollmentV2SocialRegistrationChallengeUrl(
                    provider,
                    challenge,
                    selectedProviderScheme
                )
            );
        },
        [currentStep, dispatch, maxStep, organization, socialRegistrationProviders, userInformation]
    );

    const disconnectSocialRegistration = useCallback(() => {
        dispatch(setSocialRegistrationError(null));
        dispatch(setSocialRegistrationExistingAccountProvider(null));
        dispatch(
            setUserData({
                registrationMethod: 'email',
                provider: undefined,
                externalRegistrationKey: undefined,
                socialEmail: undefined,
                emailRequired: false,
            })
        );
    }, [dispatch]);

    return (
        <div>
            <ContentfulSubheading attribute="step1Subtitle" />
            <Formik initialValues={initialValues} onSubmit={onSubmit}>
                <LeadCaptureForm
                    submitting={submitting}
                    isSocialRegistration={isSocialRegistration}
                    hasReadOnlySocialEmail={hasReadOnlySocialEmail}
                    connectedProvider={userInformation.provider}
                    providerLabel={providerLabel}
                    socialRegistrationError={socialRegistrationError}
                    socialRegistrationExistingAccountProvider={
                        socialRegistrationExistingAccountProvider
                    }
                    startSocialRegistration={startSocialRegistration}
                    disconnectSocialRegistration={disconnectSocialRegistration}
                    showSocialLoginButtons={hasAvailableSocialProviders || isSocialRegistration}
                    showGoogleSignIn={Boolean(googleProvider)}
                    showAppleSignIn={Boolean(appleProvider)}
                />
            </Formik>
        </div>
    );
};

const LeadCaptureForm = ({
    submitting,
    isSocialRegistration,
    hasReadOnlySocialEmail,
    connectedProvider,
    providerLabel: _providerLabel,
    socialRegistrationError,
    socialRegistrationExistingAccountProvider,
    startSocialRegistration,
    disconnectSocialRegistration,
    showSocialLoginButtons,
    showGoogleSignIn,
    showAppleSignIn,
}: {
    submitting: boolean;
    isSocialRegistration: boolean;
    hasReadOnlySocialEmail: boolean;
    connectedProvider?: 'google' | 'apple';
    providerLabel: string;
    socialRegistrationError: string | null;
    socialRegistrationExistingAccountProvider: 'google' | 'apple' | null;
    startSocialRegistration: (
        provider: SocialRegistrationProvider,
        values: HubspotContactInfo
    ) => void;
    disconnectSocialRegistration: () => void;
    showSocialLoginButtons: boolean;
    showGoogleSignIn: boolean;
    showAppleSignIn: boolean;
}) => {
    const { isValid, values } = useFormikContext<HubspotContactInfo>();
    const loginUrl = useMemo(
        () => `${Routes.Login}?returnUrl=${encodeURIComponent(buildPostLoginContinueReturnPath())}`,
        []
    );
    const existingAccountProviderLabel = useMemo(
        () => getSocialProviderLabel(socialRegistrationExistingAccountProvider),
        [socialRegistrationExistingAccountProvider]
    );

    return (
        <Form>
            {socialRegistrationError && (
                <>
                    <p className="text-error text-sm pb-5">{socialRegistrationError}</p>
                    <FieldSpacer className="pb-5" />
                </>
            )}
            {socialRegistrationExistingAccountProvider && (
                <>
                    <p className="text-error text-sm pb-5">
                        {existingAccountProviderLabel === 'social'
                            ? 'An account already exists for this sign-in. '
                            : `An account already exists for your ${existingAccountProviderLabel} sign-in. `}
                        <a href={loginUrl} className="underline font-normal">
                            Sign in to continue your enrollment.
                        </a>
                    </p>
                    <FieldSpacer className="pb-5" />
                </>
            )}
            {showSocialLoginButtons && (
                <>
                    <p className="text-[17px] leading-[24px] font-bold text-grey-1 pb-3">
                        Choose your sign-in method
                    </p>
                    <div className="pb-2">
                        <SocialLoginButtons
                            connectedProvider={
                                isSocialRegistration ? connectedProvider ?? null : null
                            }
                            onGoogleSignIn={
                                isSocialRegistration
                                    ? undefined
                                    : () => startSocialRegistration('google', values)
                            }
                            onAppleSignIn={
                                isSocialRegistration
                                    ? undefined
                                    : () => startSocialRegistration('apple', values)
                            }
                            onDisconnect={
                                isSocialRegistration ? disconnectSocialRegistration : undefined
                            }
                            showDivider={!hasReadOnlySocialEmail}
                            showGoogle={showGoogleSignIn}
                            showApple={showAppleSignIn}
                            googleLabel="Sign up with Google"
                            appleLabel="Sign up with Apple"
                        />
                    </div>
                </>
            )}
            {(!showSocialLoginButtons || !hasReadOnlySocialEmail) && (
                <>
                    <OrgEmailField />
                    <FieldSpacer className="pb-5" />
                </>
            )}
            <FormikTextField
                label="Organization Name"
                name="organizationName"
                placeholder="Ex: Spartan Athletics"
                validate={orgNameValidator}
            />
            <FieldSpacer className="pb-5" />
            <OrganizationTypeField />
            <FieldSpacer className="pb-5" />
            <input name="gclid" type="hidden" />
            <p className="text-[17px] leading-[24px] font-normal text-grey-1 mb-4">
                By clicking Continue below, you are stating you have read, understand, and agree to
                the{' '}
                <a
                    href={ExternalLinks.PrivacyPolicy}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand underline"
                >
                    Privacy Policy
                </a>
                ,{' '}
                <a
                    href={ExternalLinks.TermsOfUse}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand underline"
                >
                    Terms of Use
                </a>
                , and{' '}
                <a
                    href={ExternalLinks.OrganizationTermsOfUse}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand underline"
                >
                    Organization Terms of Use
                </a>
                .
            </p>
            <div className="flex sm:place-content-start place-content-center">
                <FormBtn
                    className="org_enrollment_step_one "
                    disabled={!isValid}
                    isSubmitting={submitting}
                >
                    Continue
                </FormBtn>
            </div>
        </Form>
    );
};

const OrganizationTypeField = () => {
    const orgTypes = useRootSelector(selectOrgTypeOptions);
    const loading = useRootSelector(selectIsOrganizationTypesLoading);
    const formikProps = useFormikContext<HubspotContactInfo>();

    return (
        <FormikSelectField<HubspotContactInfo>
            name="organizationTypeId"
            label="Organization Type"
            validate={orgTypeValidator}
            isLoading={loading}
            className={formikProps.values.organizationTypeId ? 'text-grey-1' : 'text-grey-3'}
        >
            <option value="">Ex: Private School, Athletic, etc.</option>
            {orgTypes?.map((orgType) => (
                <option key={orgType.id} value={orgType.id}>
                    {orgType.description}
                </option>
            ))}
        </FormikSelectField>
    );
};

const OrgEmailField = () => {
    const userInformation = useRootSelector(selectUserInformation);
    const isSocialRegistration =
        userInformation.registrationMethod === 'social' &&
        Boolean(userInformation.externalRegistrationKey);
    const hasReadOnlySocialEmail =
        isSocialRegistration &&
        !userInformation.emailRequired &&
        Boolean(userInformation.socialEmail);
    const providerLabel = useMemo(() => {
        if (userInformation.provider === 'apple') {
            return 'Apple';
        }

        if (userInformation.provider === 'google') {
            return 'Google';
        }

        return 'your social account';
    }, [userInformation.provider]);

    return (
        <div>
            <FormikTextField
                name="coordinatorEmailAddress"
                type="email"
                label="Email Address"
                placeholder="Ex: joey@spartanathletics.com"
                validate={
                    !isSocialRegistration || userInformation.emailRequired
                        ? orgEnrollEmailValidator
                        : undefined
                }
                disabled={hasReadOnlySocialEmail}
            />
            <HelpTextOrFieldErrorSection
                isSocialRegistration={isSocialRegistration}
                hasReadOnlySocialEmail={hasReadOnlySocialEmail}
                providerLabel={providerLabel}
            />
        </div>
    );
};

const HelpTextOrFieldErrorSection = ({
    isSocialRegistration,
    hasReadOnlySocialEmail,
    providerLabel,
}: {
    isSocialRegistration: boolean;
    hasReadOnlySocialEmail: boolean;
    providerLabel: string;
}) => {
    const userInformation = useRootSelector(selectUserInformation);
    const { errors, submitCount } = useFormikContext<HubspotContactInfo>();
    const loginUrl = useMemo(() => getUrlToLoginPage(Routes.StartProgramContinue), []);

    return (
        <div>
            <FadeIn when={errors.coordinatorEmailAddress === ' ' && submitCount > 0}>
                <p className="text-error text-xs">
                    An account with that email already exists.{' '}
                    <a href={loginUrl} className="underline font-normal">
                        Sign in.
                    </a>
                </p>
            </FadeIn>
            <FadeIn when={!(submitCount > 0 && Boolean(errors.coordinatorEmailAddress))}>
                <p className="mt-1 text-xs text-grey-medium">
                    {hasReadOnlySocialEmail
                        ? `This email address came from ${providerLabel}. Disconnect ${providerLabel} to choose a different email address.`
                        : isSocialRegistration && userInformation.emailRequired
                        ? `We could not get an email from ${providerLabel}. Please provide the email address you want to use to log in.`
                        : 'This will be the email address that you use to log in to your account. Use an email address that is associated with your organization, if possible.'}
                </p>
            </FadeIn>
        </div>
    );
};
